import { classnames, CssProp } from '@core/utils/css';
import React, { ButtonHTMLAttributes, Fragment, memo, PropsWithChildren, ReactNode, useMemo } from 'react';
import { WithTooltip } from '@modules/common';

export type ButtonIcon = 'next' | 'prev' | 'add' | 'save' | (ReactNode & {});
export type ButtonIntent = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'dark' | 'light' | 'link';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  customCss?: CssProp;
  intent?: ButtonIntent;
  loading?: boolean;
  icon?: string;
  leftIcon?: string;
  block?: boolean;
  extraClassNames?: string;
  outline?: boolean;
  iconExtraClasses?: string;
  tooltip?: string;
  size?: 'small';
}

export const Button = memo((props: PropsWithChildren<ButtonProps>) => {
  const {
    children,
    customCss,
    intent = 'primary',
    loading,
    leftIcon,
    icon,
    block,
    extraClassNames = '',
    iconExtraClasses = '',
    outline,
    size,
    ...rest
  } = props;

  const leftContent = useMemo(() => getIcon(props.leftIcon, iconExtraClasses), [iconExtraClasses, props.leftIcon]);
  const rightContent = useMemo(() => getIcon(props.icon, iconExtraClasses), [iconExtraClasses, props.icon]);

  const disabled = loading || rest.disabled;

  const content = loading ? (
    <Fragment>
      <span className="content">
        <span className="contentOriginal">{children}</span> <i className="bx bx-loader bx-spin font-size-16 align-middle mr-2"></i>{' '}
      </span>
    </Fragment>
  ) : (
    <Fragment>
      {leftContent} {children} {rightContent}
    </Fragment>
  );

  const btnClasses = classnames(
    'btn',
    `btn-${outline ? 'outline-' : ''}${intent}`,
    'waves-effect',
    'waves-light',
    !!block && 'btn-block',
    size === 'small' && 'btn-sm',
    extraClassNames,
  );

  const buttonContent = (
    <button type="submit" {...rest} disabled={disabled} className={btnClasses}>
      {content}
    </button>
  );

  if (props.tooltip) {
    return <WithTooltip tooltip={props.tooltip}>{buttonContent}</WithTooltip>;
  }
  return buttonContent;
});

function getIcon(icon?: string, iconExtraClasses?: string) {
  if (!icon) return null;
  return <i className={`font-size-16 align-middle mr-2 ${iconExtraClasses} ${icon}`}></i>;
}
